import type { CartesianPose } from '@sb/geometry';
import type { EquipmentItem } from '@sb/integrations/types';
import type { TCPFrame, TCPOffsetOption } from '@sb/motion-planning';

import { resolveTCPFrame } from './util/tcpFrame';

/**
 * The state this rule reads, structurally — the routine runner's own
 * `configuration` (where the user-intent Active Tooltip lives), and the shape
 * a UI holds a copy of. Optional because `RoutineContext` reaches this rule
 * during construction, before its state exists.
 */
type ActiveTooltipState = { readonly activeTooltip?: TCPFrame } | undefined;

/**
 * The robot's Active Tooltip, but only while it names `tcpOption`.
 *
 * The Active Tooltip is a (option, `gripperID`) pair, and the id identifies the
 * operator's chosen device — so it belongs to that one frame. A transient frame
 * some step repointed to keeps resolving by option name against enabled
 * equipment, however the pose it produces is later read.
 *
 * This is the single definition of that pairing. Both the routine runner
 * (resolving poses for motion) and the pose capture the UI stamps onto saved
 * positions read it here, so a pose is stamped with the id it will be resolved
 * through.
 */
export function activeTooltipForFrame(
  configuration: ActiveTooltipState,
  tcpOption: TCPOffsetOption,
): TCPFrame | undefined {
  const activeTooltip = configuration?.activeTooltip;

  return activeTooltip?.tcpOption === tcpOption ? activeTooltip : undefined;
}

/**
 * The `gripperID` {@link activeTooltipForFrame} lends to `tcpOption`, for
 * callers that resolve a pose rather than inspect the frame. `undefined` — the
 * Active Tooltip names another frame, or names this one without an id — reads
 * as "resolve the option name against enabled equipment" everywhere a
 * `gripperID` is accepted.
 */
export function activeTooltipGripperIDForFrame(
  configuration: ActiveTooltipState,
  tcpOption: TCPOffsetOption,
): string | undefined {
  return activeTooltipForFrame(configuration, tcpOption)?.gripperID;
}

/**
 * The flange→TCP transform of the Active Tooltip on `tcpOption`: the controlled
 * point, as the trajectory controller's `commanded_frame` wants it. Identity
 * when that resolves to the wrist, leaving the flange as the point.
 *
 * Pairs the option with {@link activeTooltipGripperIDForFrame} before
 * resolving, so it is the one place a jog and a stream both get the point
 * from — two call sites resolving it themselves is how they drift.
 */
export function activeTooltipTransform(
  equipmentItems: EquipmentItem[],
  configuration: ActiveTooltipState,
  tcpOption: TCPOffsetOption,
): CartesianPose {
  return resolveTCPFrame(equipmentItems, {
    tcpOption,
    gripperID: activeTooltipGripperIDForFrame(configuration, tcpOption),
  }).transformFromTool0;
}
